import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    Select,
    Button,
    Table,
    Tag,
    Space,
    Typography,
    Tooltip,
    Alert,
    Modal,
    Row,
    Col,
    Radio,
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    SwapRightOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined,
    LockOutlined,
    ProfileOutlined,
    InfoCircleOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest } from 'api';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import { ClassLevelSelect } from 'Components';
import './ClassActualSettleBoard.css';

const { Text } = Typography;
const { Option } = Select;

interface SettleItem {
    incomeSchoolId: number;
    incomeSchoolCode: string;
    incomeSchoolName: string;
    unitPrice: number;
    expectedUnits: number;
    expectedAmount: number;
    actualUnits: number;
    actualAmount: number;
    diffUnits: number;
    diffAmount: number;
}

interface SettleStudent {
    studentClassYearId: number;
    studentId: number;
    studentCode: string;
    studentName: string;
    attendanceUnits?: number;
    items: SettleItem[];
}

interface ActualSettleBoardData {
    classYearId: number;
    className: string;
    schoolYear: string;
    month: number;
    year: number;
    settleStatus: number; // 0: chưa chốt, 1: đã chốt thực tế, 2: đã chuyển kỳ
    settledBy?: string;
    settledDate?: string;
    rateSources: Array<{ id: number; code: string; name: string }>;
    students: SettleStudent[];
}

interface ClassYearOption {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
}

export const ClassActualSettleBoard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(() => dayjs(`${getStoredWorkMonth().year}`, 'YYYY'));
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | null>(null);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);

    const [classList, setClassList] = useState<ClassYearOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [settling, setSettling] = useState<boolean>(false);
    const [boardData, setBoardData] = useState<ActualSettleBoardData | null>(null);
    const [filterSourceId, setFilterSourceId] = useState<number | 'all'>('all');

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            const { month, year } = e.detail || getStoredWorkMonth();
            setSelectedMonth(month);
            setSelectedYear(dayjs(`${year}`, 'YYYY'));
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    // Fetch class list
    const fetchClasses = useCallback(async (year: number) => {
        if (!schoolId) return;
        try {
            const res: any = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${year}&schoolId=${schoolId}&page=1&perPage=200&sort=asc&sortBy=className`
            );
            if (res?.code === 200 && res.info?.list) {
                setClassList(res.info.list);
                const paramClassId = searchParams.get('classYearId');
                if (paramClassId) {
                    const id = parseInt(paramClassId, 10);
                    if (res.info.list.some((c: any) => c.classYearId === id)) {
                        setSelectedClassYearId(id);
                        return;
                    }
                }
                if (!selectedClassYearId && res.info.list.length > 0) {
                    setSelectedClassYearId(res.info.list[0].classYearId);
                }
            }
        } catch {
            toast.error('Lỗi khi tải danh sách lớp');
        }
    }, [schoolId, searchParams, selectedClassYearId]);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses(selectedYear.year());
        }
    }, [selectedYear, fetchClasses]);

    const filteredClassList = useMemo(() => {
        if (!selectedClassLevel) return classList;
        return classList.filter((c) => String(c.classLevel).trim() === selectedClassLevel.trim());
    }, [classList, selectedClassLevel]);

    const handleClassLevelChange = (level?: string) => {
        setSelectedClassLevel(level);
        const nextList = level
            ? classList.filter((c) => String(c.classLevel).trim() === level.trim())
            : classList;
        if (nextList.length > 0) {
            if (!nextList.some((c) => c.classYearId === selectedClassYearId)) {
                setSelectedClassYearId(nextList[0].classYearId);
                setSearchParams({ classYearId: String(nextList[0].classYearId) });
            }
        } else {
            setSelectedClassYearId(null);
            setBoardData(null);
        }
    };

    // Fetch actual settle data
    const fetchSettleData = useCallback(async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setLoading(true);
        try {
            const yearNum = selectedYear.year();
            const res: any = await getRequest(
                `/cms/accountant/class-income/actual-settle/board?classYearId=${selectedClassYearId}&month=${selectedMonth}&year=${yearNum}`
            );
            if (res?.code === 200 && res.info) {
                setBoardData(res.info);
            } else {
                toast.error(res?.msg || 'Không thể tải dữ liệu chốt thực tế');
            }
        } catch {
            toast.error('Lỗi kết nối khi tải dữ liệu chốt thực tế');
        } finally {
            setLoading(false);
        }
    }, [selectedClassYearId, selectedMonth, selectedYear]);

    useEffect(() => {
        fetchSettleData();
    }, [fetchSettleData]);

    // Unified Button: [Chốt thực tế]
    const handleSyncAndFinalize = async () => {
        if (!selectedClassYearId || !selectedYear) {
            toast.warning('Vui lòng chọn lớp học và năm học!');
            return;
        }
        setSettling(true);
        try {
            const res: any = await postRequest('/cms/accountant/class-income/actual-settle/sync-and-finalize', {
                classYearId: selectedClassYearId,
                month: selectedMonth,
                year: selectedYear.year(),
            });
            if (res?.code === 200) {
                toast.success('Chốt thực tế thành công! Đã đồng bộ số ngày công và tính toán thừa thiếu.');
                if (res.info && typeof res.info === 'object' && res.info.students) {
                    setBoardData(res.info);
                } else {
                    await fetchSettleData();
                }
            } else {
                toast.error(res?.msg || 'Lỗi khi chốt thực tế');
            }
        } catch {
            toast.error('Lỗi kết nối khi chốt thực tế');
        } finally {
            setSettling(false);
        }
    };

    // Flatten data for table view (one row per student fee item)
    const flatTableData = useMemo(() => {
        if (!boardData || !boardData.students) return [];
        const rows: any[] = [];
        let stt = 1;
        const isSettled = (boardData.settleStatus || 0) >= 1;

        boardData.students.forEach((st) => {
            (st.items || []).forEach((item) => {
                if (filterSourceId !== 'all' && item.incomeSchoolId !== filterSourceId) {
                    return;
                }
                const hasActual = item.actualUnits != null;
                rows.push({
                    key: `${st.studentClassYearId}_${item.incomeSchoolId}`,
                    stt: stt++,
                    studentClassYearId: st.studentClassYearId,
                    studentCode: st.studentCode,
                    studentName: st.studentName || (st as any).fullName || '',
                    attendanceUnits: st.attendanceUnits || 0,
                    incomeSchoolId: item.incomeSchoolId,
                    incomeSchoolCode: item.incomeSchoolCode,
                    incomeSchoolName: item.incomeSchoolName,
                    unitPrice: item.unitPrice,
                    expectedUnits: item.expectedUnits,
                    expectedAmount: item.expectedAmount,
                    actualUnits: item.actualUnits,
                    actualAmount: item.actualAmount,
                    diffUnits: item.diffUnits,
                    diffAmount: item.diffAmount,
                    hasActual,
                    isSettled,
                });
            });
        });
        return rows;
    }, [boardData, filterSourceId]);

    // Aggregate statistics
    const stats = useMemo(() => {
        let totalExpected = 0;
        let totalActual = 0;
        let totalExcess = 0;
        let totalDebt = 0;

        flatTableData.forEach((r) => {
            totalExpected += r.expectedAmount || 0;
            if (r.actualAmount != null) {
                totalActual += r.actualAmount;
            }
            if (r.diffAmount != null) {
                if (r.diffAmount > 0) {
                    totalExcess += r.diffAmount;
                } else if (r.diffAmount < 0) {
                    totalDebt += Math.abs(r.diffAmount);
                }
            }
        });

        return { totalExpected, totalActual, totalExcess, totalDebt };
    }, [flatTableData]);

    const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
    const nextYear = selectedMonth === 12 ? (selectedYear?.year() || 2026) + 1 : (selectedYear?.year() || 2026);

    const columns: ColumnsType<any> = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 50,
            align: 'center',
            fixed: 'left',
        },
        {
            title: 'Mã HS',
            dataIndex: 'studentCode',
            key: 'studentCode',
            width: 100,
            align: 'center',
            fixed: 'left',
            render: (val) => <Text strong>{val || '-'}</Text>,
        },
        {
            title: 'Họ và tên',
            dataIndex: 'studentName',
            key: 'studentName',
            width: 170,
            fixed: 'left',
            render: (val, r) => <Text strong style={{ color: '#262626' }}>{val || r.fullName || ''}</Text>,
        },
        {
            title: 'Khoản thu',
            dataIndex: 'incomeSchoolName',
            key: 'incomeSchoolName',
            width: 180,
            render: (val) => <Tag color="orange" style={{ fontSize: 12 }}>{val}</Tag>,
        },
        {
            title: 'Đơn giá',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: 100,
            align: 'right',
            render: (val) => Number(val || 0).toLocaleString('vi-VN'),
        },
        {
            title: 'Đã thu đầu tháng',
            children: [
                {
                    title: 'Số suất thu',
                    dataIndex: 'expectedUnits',
                    key: 'expectedUnits',
                    width: 90,
                    align: 'right',
                    render: (val) => <Text strong>{val ?? 0}</Text>,
                },
                {
                    title: 'Tiền đã thu',
                    dataIndex: 'expectedAmount',
                    key: 'expectedAmount',
                    width: 110,
                    align: 'right',
                    render: (val) => (
                        <Text strong style={{ color: '#1890ff' }}>
                            {Number(val || 0).toLocaleString('vi-VN')}
                        </Text>
                    ),
                },
            ],
        },
        {
            title: 'Chấm ngày',
            dataIndex: 'attendanceUnits',
            key: 'attendanceUnits',
            width: 100,
            align: 'center',
            render: (val) => (
                <Tag color="cyan" style={{ fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                    {val ?? 0} ngày
                </Tag>
            ),
        },
        {
            title: 'Thực tế (Sau khi chốt)',
            children: [
                {
                    title: 'Suất thực tế',
                    dataIndex: 'actualUnits',
                    key: 'actualUnits',
                    width: 110,
                    align: 'right',
                    render: (val) => {
                        if (val == null) {
                            return <Tag style={{ color: '#8c8c8c', background: '#f5f5f5' }}>Chưa chốt</Tag>;
                        }
                        return (
                            <Tag color="blue" style={{ fontSize: 13, fontWeight: 700 }}>
                                {val}
                            </Tag>
                        );
                    },
                },
                {
                    title: 'Tiền thực tế',
                    dataIndex: 'actualAmount',
                    key: 'actualAmount',
                    width: 110,
                    align: 'right',
                    render: (val) => {
                        if (val == null) {
                            return <Text type="secondary">-</Text>;
                        }
                        return (
                            <Text strong style={{ color: '#722ed1' }}>
                                {Number(val || 0).toLocaleString('vi-VN')}
                            </Text>
                        );
                    },
                },
            ],
        },
        {
            title: `Thừa / Thiếu sang Tháng ${nextMonth}`,
            children: [
                {
                    title: 'Lệch suất',
                    dataIndex: 'diffUnits',
                    key: 'diffUnits',
                    width: 90,
                    align: 'right',
                    render: (val) => {
                        if (val == null) {
                            return <Text type="secondary">-</Text>;
                        }
                        if (val > 0) return <Text strong style={{ color: '#389e0d' }}>+{val}</Text>;
                        if (val < 0) return <Text strong style={{ color: '#cf1322' }}>{val}</Text>;
                        return <Text type="secondary">0</Text>;
                    },
                },
                {
                    title: 'Tiền kết chuyển',
                    dataIndex: 'diffAmount',
                    key: 'diffAmount',
                    width: 130,
                    align: 'right',
                    render: (val) => {
                        if (val == null) {
                            return <Tag style={{ color: '#8c8c8c' }}>Chờ chốt</Tag>;
                        }
                        if (val > 0) {
                            return (
                                <Tag className="diff-tag-excess" style={{ fontSize: 12 }}>
                                    +{Number(val).toLocaleString('vi-VN')} (Thừa)
                                </Tag>
                            );
                        }
                        if (val < 0) {
                            return (
                                <Tag className="diff-tag-debt" style={{ fontSize: 12 }}>
                                    {Number(val).toLocaleString('vi-VN')} (Nợ)
                                </Tag>
                            );
                        }
                        return <Tag style={{ fontSize: 12 }}>0 đ</Tag>;
                    },
                },
            ],
        },
    ];

    const lastSettleInfo = useMemo(() => {
        if (!boardData?.settledDate) return null;
        const d = dayjs(boardData.settledDate);
        const timeStr = d.isValid() ? d.format('DD/MM/YYYY HH:mm') : boardData.settledDate;
        return `${timeStr}${boardData.settledBy ? ` (${boardData.settledBy})` : ''}`;
    }, [boardData?.settledDate, boardData?.settledBy]);

    return (
        <div className="class-actual-settle-page">
            {/* TOOLBAR */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 8,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    marginBottom: 10,
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: '10px 16px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    {/* Left: Filters */}
                    <Space wrap size={16} align="center">

                        <Button
                            icon={<ProfileOutlined />}
                            onClick={() => navigate(`/class-month-attendance?classYearId=${selectedClassYearId || ''}`)}
                        >
                            Chấm ngày
                        </Button>

                        <Tag
                            color="blue"
                            style={{
                                fontSize: 13,
                                padding: '4px 12px',
                                borderRadius: 4,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                            }}
                        >
                            <CalendarOutlined /> Tháng {selectedMonth}/{selectedYear ? selectedYear.year() : ''}
                        </Tag>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Khối:</Text>
                            <ClassLevelSelect
                                value={selectedClassLevel}
                                onChange={handleClassLevelChange}
                                placeholder="Tất cả khối"
                                style={{ width: 130 }}
                            />
                        </Space>

                        <Space size={6} align="center">
                            <Text strong style={{ color: '#595959', fontSize: 13 }}>Lớp:</Text>
                            <Select
                                value={selectedClassYearId || undefined}
                                onChange={(val) => {
                                    setSelectedClassYearId(val);
                                    setSearchParams({ classYearId: String(val) });
                                }}
                                placeholder="Chọn lớp"
                                style={{ width: 160 }}
                                showSearch
                                optionFilterProp="children"
                            >
                                {filteredClassList.map((c) => (
                                    <Option key={c.classYearId} value={c.classYearId}>
                                        {c.className}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        {/* Filter by Rate Source */}
                        {boardData && boardData.rateSources && boardData.rateSources.length > 1 && (
                            <Space size={6} align="center">
                                <Text strong style={{ color: '#595959', fontSize: 13 }}>Khoản thu:</Text>
                                <Select
                                    value={filterSourceId}
                                    onChange={(v) => setFilterSourceId(v)}
                                    style={{ width: 160 }}
                                >
                                    <Option value="all">Tất cả nguồn thu</Option>
                                    {boardData.rateSources.map((rs) => (
                                        <Option key={rs.id} value={rs.id}>{rs.name}</Option>
                                    ))}
                                </Select>
                            </Space>
                        )}

                        <Tooltip title="Tải lại dữ liệu">
                            <Button icon={<ReloadOutlined />} onClick={fetchSettleData} loading={loading} />
                        </Tooltip>
                    </Space>

                    {/* Right: Actions */}
                    <Space wrap size={12} align="center">
                        {/* UNIFIED ACTION BUTTON 1: [Chốt thực tế] */}
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={settling}
                            onClick={handleSyncAndFinalize}
                            disabled={boardData?.settleStatus === 2}
                            style={{
                                backgroundColor: '#52c41a',
                                borderColor: '#52c41a',
                                fontWeight: 600,
                                borderRadius: 6,
                                boxShadow: '0 2px 6px rgba(82, 196, 26, 0.35)',
                            }}
                        >
                            Chốt thực tế (Lấy từ chấm ngày)
                        </Button>

                        {/* ACTION BUTTON 2: Sang Màn hình kết chuyển số dư */}
                        <Button
                            type="primary"
                            icon={<SwapRightOutlined />}
                            onClick={() => navigate(`/class-settle-carry-forward?classYearId=${selectedClassYearId || ''}`)}
                            style={{
                                backgroundColor: '#722ed1',
                                borderColor: '#722ed1',
                                fontWeight: 600,
                                borderRadius: 6,
                                boxShadow: '0 2px 6px rgba(114, 46, 209, 0.35)',
                            }}
                        >
                            Kết chuyển số dư
                        </Button>
                    </Space>
                </div>

                {/* Settle Status Banner - Nhỏ gọn, hiển thị lần chốt & lưu ý nguồn thu suất/tháng */}
                {boardData && (
                    <div style={{ marginTop: 8 }}>
                        {boardData.settleStatus === 0 && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ padding: '6px 12px', fontSize: 12.5, borderRadius: 6 }}
                                message={
                                    <span>
                                        <strong>Chưa chốt thực tế Tháng {selectedMonth}/{selectedYear?.year()}:</strong> Bấm <strong style={{ color: '#389e0d' }}>[Chốt thực tế (Lấy từ chấm ngày)]</strong> để tính thừa/thiếu theo số ngày đi học.
                                    </span>
                                }
                            />
                        )}
                        {boardData.settleStatus === 1 && (
                            <Alert
                                type="success"
                                showIcon
                                style={{ padding: '6px 12px', fontSize: 12.5, borderRadius: 6 }}
                                message={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <Space size={12} wrap>
                                            <strong>Đã chốt thực tế Tháng {selectedMonth}/{selectedYear?.year()}</strong>
                                            {lastSettleInfo && (
                                                <span style={{ color: '#595959', fontSize: 12 }}>
                                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                    Lần chốt gần nhất: <strong>{lastSettleInfo}</strong>
                                                </span>
                                            )}
                                        </Space>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => navigate(`/class-settle-carry-forward?classYearId=${selectedClassYearId || ''}`)}
                                            style={{ padding: 0, fontWeight: 600, color: '#722ed1' }}
                                        >
                                            Sang Kết chuyển số dư &rarr;
                                        </Button>
                                    </div>
                                }
                            />
                        )}
                        {boardData.settleStatus === 2 && (
                            <Alert
                                type="warning"
                                showIcon
                                icon={<LockOutlined />}
                                style={{ padding: '6px 12px', fontSize: 12.5, borderRadius: 6 }}
                                message={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <Space size={12} wrap>
                                            <strong style={{ color: '#d46b08' }}>Đã kết chuyển sang Tháng {nextMonth}/{nextYear} (Khóa sổ)</strong>
                                            {lastSettleInfo && (
                                                <span style={{ color: '#595959', fontSize: 12 }}>
                                                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                    Lần chốt gần nhất: <strong>{lastSettleInfo}</strong>
                                                </span>
                                            )}
                                        </Space>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => navigate(`/class-settle-carry-forward?classYearId=${selectedClassYearId || ''}`)}
                                            style={{ padding: 0, fontWeight: 600, color: '#722ed1' }}
                                        >
                                            Xem Kết chuyển số dư &rarr;
                                        </Button>
                                    </div>
                                }
                            />
                        )}

                        {/* Ghi chú lưu ý phạm vi tính toán */}
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#595959' }}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                            <span>
                                <strong>Lưu ý:</strong> Màn hình này chỉ tính toán đối soát cho các nguồn thu <strong>suất/tháng</strong> (tiền ăn, bán trú,... dựa vào số ngày chấm công thực tế). Các khoản thu khác xem tại màn hình <strong>Kết chuyển số dư</strong>.
                            </span>
                        </div>
                    </div>
                )}
            </Card>

            {/* COMPACT SUMMARY BAR - Thanh thống kê nhỏ gọn tối ưu không gian */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 8,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    marginBottom: 10,
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                }}
                bodyStyle={{ padding: '8px 16px' }}
            >
                <Row gutter={[16, 6]} align="middle" justify="space-between">
                    <Col xs={12} sm={6}>
                        <Space size={6} wrap>
                            <span style={{ fontSize: 12.5, color: '#595959' }}>Đã thu đầu tháng:</span>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1890ff' }}>
                                {stats.totalExpected.toLocaleString('vi-VN')} đ
                            </span>
                        </Space>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Space size={6} wrap>
                            <span style={{ fontSize: 12.5, color: '#595959' }}>Thực tế:</span>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#722ed1' }}>
                                {stats.totalActual.toLocaleString('vi-VN')} đ
                            </span>
                        </Space>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Space size={6} wrap>
                            <span style={{ fontSize: 12.5, color: '#389e0d' }}>Tổng thừa (Khấu trừ T{nextMonth}):</span>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#389e0d' }}>
                                +{stats.totalExcess.toLocaleString('vi-VN')} đ
                            </span>
                        </Space>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Space size={6} wrap>
                            <span style={{ fontSize: 12.5, color: '#cf1322' }}>Tổng thiếu (Thu thêm T{nextMonth}):</span>
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#cf1322' }}>
                                -{stats.totalDebt.toLocaleString('vi-VN')} đ
                            </span>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* DATA TABLE */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 8,
                    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                    background: '#ffffff',
                }}
                bodyStyle={{ padding: 10 }}
            >
                <Table
                    columns={columns}
                    dataSource={flatTableData}
                    rowKey="key"
                    loading={loading}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1100, y: 'calc(100vh - 280px)' }}
                />
            </Card>

            {/* CONFIRM CARRY FORWARD MODAL */}
        </div>
    );
};

export default ClassActualSettleBoard;
